import { ref, computed, isRef, watch } from 'vue';
import { t } from '@/utils/i18n-helper';
import invitacionesService from '@/services/perfil/invitaciones';
import { toError } from '@/services/perfil/_normalize';
import { useFetchPaginated } from '@/composables/front/common/useFetchPaginated';

/**
 * useInvitaciones — Dashboard inicial de invitaciones del abonado
 *
 * Gestiona el ciclo de vida del dashboard: stats, eventos disponibles y
 * historial paginado de consumidas. Recibe el ID del abonado como ref o valor
 * reactivo (igual que useLiberarAsiento), y reacciona a sus cambios.
 *
 * Contrato de uso:
 *   const {
 *     // State
 *     stats, loadingStats, errorStats,
 *     eventos, loadingEventos, errorEventos,
 *     consumidas, loadingConsumidasModal, errorConsumidas,
 *     consumidasMeta,
 *     showConsumidasModal,
 *
 *     // Computed guards
 *     hasEventos, isEventosEmpty, hasConsumidasEnModal,
 *
 *     // Métodos
 *     fetchDashboard,
 *     openConsumidasModal, closeConsumidasModal,
 *     fetchConsumidasPagina,
 *   } = useInvitaciones(abonoIdSource);
 *
 * @param {import('vue').Ref<string|number>|string|number} abonadoIdSource  ID del abonado
 */
export function useInvitaciones(abonadoIdSource) {
    // ─── Stats ───────────────────────────────────────────────────────────────
    /** @type {import('vue').Ref<{ total: number, consumidas: number, restantes: number }|null>} */
    const stats = ref(null);
    const loadingStats = ref(false);
    const errorStats = ref(null);

    // ─── Eventos disponibles ──────────────────────────────────────────────────
    /** @type {import('vue').Ref<Array>} */
    const eventos = ref([]);
    const loadingEventos = ref(false);
    const errorEventos = ref(null);

    // ─── Consumidas (historial paginado) — solo se carga al abrir el modal ───
    /** @type {import('vue').Ref<Array>} */
    const consumidas = ref([]);
    const loadingConsumidasModal = ref(false);
    const errorConsumidas = ref(null);
    const consumidasMeta = ref({
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
    });

    // ─── Estado UI del modal de historial ────────────────────────────────────
    const showConsumidasModal = ref(false);

    // ─── Race-condition guard (patrón del proyecto) ───────────────────────────
    let lastAbonadoId = null;

    // ─── Helpers ─────────────────────────────────────────────────────────────
    const getAbonadoId = () => {
        if (isRef(abonadoIdSource)) return abonadoIdSource.value;
        if (typeof abonadoIdSource === 'function') return abonadoIdSource();
        return abonadoIdSource;
    };

    const resetDashboard = () => {
        stats.value = null;
        eventos.value = [];
        errorStats.value = null;
        errorEventos.value = null;
        showConsumidasModal.value = false;
        consumidas.value = [];
        consumidasMeta.value = { current_page: 1, last_page: 1, total: 0, per_page: 10 };
        errorConsumidas.value = null;
        lastAbonadoId = null;
    };

    // ─── Computed guards ──────────────────────────────────────────────────────

    /**
     * `true` mientras cualquiera de los fetches iniciales del dashboard está en curso.
     * Útil para mostrar un skeleton global en `InvitacionesView.vue` sin necesidad de
     * combinar `loadingStats || loadingEventos` en la plantilla.
     */
    const isDashboardLoading = computed(
        () => loadingStats.value || loadingEventos.value
    );

    /**
     * `true` si al menos uno de los fetches del dashboard terminó en error.
     * La UI puede mostrar un bloque de error global o errores granulares usando
     * `errorStats` / `errorEventos` individualmente.
     */
    const hasDashboardError = computed(
        () => Boolean(errorStats.value) || Boolean(errorEventos.value)
    );

    /**
     * `true` cuando stats se cargaron sin error pero el objeto es nulo o inválido.
     * Indica un estado vacío/inesperado en las stats del abonado.
     */
    const isStatsEmpty = computed(
        () => !loadingStats.value && !errorStats.value && !stats.value
    );

    /**
     * `true` cuando el abonado tiene al menos una invitación restante de temporada.
     * Guard de entrada al flujo: si es `false`, el botón "Generar" no debe activarse
     * aunque el evento tenga restantes propios.
     */
    const hasInvitacionesDisponibles = computed(
        () => stats.value !== null && (stats.value.restantes ?? 0) > 0
    );

    /**
     * `true` cuando hay al menos un evento disponible y no hay carga ni error.
     */
    const hasEventos = computed(
        () => !loadingEventos.value && !errorEventos.value && eventos.value.length > 0
    );

    /**
     * `true` cuando la petición terminó sin error pero no hay eventos (empty state).
     */
    const isEventosEmpty = computed(
        () => !loadingEventos.value && !errorEventos.value && eventos.value.length === 0
    );

    /**
     * Devuelve `true` si un evento concreto permite generar invitaciones.
     * Condiciones: el evento tiene `invitaciones_restantes > 0` Y
     * `all_invitaciones_consumidas` no es `true`.
     *
     * La UI usa esta función para activar/desactivar el botón "Generar"
     * por cada card de evento sin inferir la lógica directamente en la plantilla.
     *
     * @param {Object} eventoItem  Objeto evento del listado
     * @returns {boolean}
     */
    const canGenerarInvitaciones = eventoItem => {
        if (!eventoItem) return false;
        const restantes = eventoItem.invitaciones_restantes ?? 0;
        const agotadas  = eventoItem.all_invitaciones_consumidas === true;
        return restantes > 0 && !agotadas;
    };

    /**
     * `true` cuando el modal tiene consumidas cargadas y sin error.
     */
    const hasConsumidasEnModal = computed(
        () => !loadingConsumidasModal.value && !errorConsumidas.value && consumidas.value.length > 0
    );

    /**
     * `true` cuando el modal está visible, la carga terminó sin error, pero no hay items.
     * Permite mostrar el empty-state dentro del modal sin condiciones compuestas en plantilla.
     */
    const isConsumidasModalEmpty = computed(
        () => showConsumidasModal.value && !loadingConsumidasModal.value && !errorConsumidas.value && consumidas.value.length === 0
    );

    /**
     * `true` cuando hay más páginas disponibles en el historial.
     */
    const hasMoreConsumidasPages = computed(
        () => consumidasMeta.value.current_page < consumidasMeta.value.last_page
    );

    // ─── Fetches individuales ─────────────────────────────────────────────────

    const _fetchStats = async abonadoId => {
        loadingStats.value = true;
        errorStats.value = null;
        try {
            const result = await invitacionesService.getStats(abonadoId);
            if (lastAbonadoId !== abonadoId) return;
            stats.value = result;
        } catch (err) {
            if (lastAbonadoId !== abonadoId) return;
            const normalized = toError(err);
            errorStats.value = normalized.message ??
                t('perfil.invitaciones.error_stats', {}, 'Error al cargar las estadísticas de invitaciones.');
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useInvitaciones] Error al obtener stats:', err);
            }
        } finally {
            if (lastAbonadoId === abonadoId) {
                loadingStats.value = false;
            }
        }
    };

    const _fetchEventos = async abonadoId => {
        loadingEventos.value = true;
        errorEventos.value = null;
        try {
            const result = await invitacionesService.getEventos(abonadoId);
            if (lastAbonadoId !== abonadoId) return;
            eventos.value = result;
        } catch (err) {
            if (lastAbonadoId !== abonadoId) return;
            const normalized = toError(err);
            errorEventos.value = normalized.message ??
                t('perfil.invitaciones.error_eventos', {}, 'Error al cargar los eventos disponibles.');
            eventos.value = [];
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useInvitaciones] Error al obtener eventos:', err);
            }
        } finally {
            if (lastAbonadoId === abonadoId) {
                loadingEventos.value = false;
            }
        }
    };

    // ─── Carga del dashboard completo ────────────────────────────────────────

    /**
     * Carga en paralelo stats y eventos del abonado.
     * Llama automáticamente al montar si abonadoId está disponible,
     * y también puede invocarse manualmente (p. ej. tras un error).
     */
    const fetchDashboard = async() => {
        const abonadoId = getAbonadoId();
        if (!abonadoId) return;

        lastAbonadoId = abonadoId;

        await Promise.all([
            _fetchStats(abonadoId),
            _fetchEventos(abonadoId),
        ]);
    };

    // ─── Modal de historial paginado ─────────────────────────────────────────

    /**
     * Abre el modal del historial de consumidas y carga la primera página
     * solo si aún no está cargada para este abonado.
     */
    const openConsumidasModal = async() => {
        showConsumidasModal.value = true;
        if (consumidas.value.length === 0 && !errorConsumidas.value) {
            await fetchConsumidasPagina(1);
        }
    };

    /**
     * Cierra el modal de historial. No descarga los datos para evitar flicker
     * si el usuario lo abre de nuevo en la misma sesión.
     */
    const closeConsumidasModal = () => {
        showConsumidasModal.value = false;
    };

    /**
     * Carga una página específica del historial de consumidas.
     * Reemplaza los items del modal con los de la página solicitada.
     *
     * @param {number} page  Número de página (1-based)
     */
    const fetchConsumidasPagina = async(page = 1) => {
        const abonadoId = getAbonadoId();
        if (!abonadoId) return;

        loadingConsumidasModal.value = true;
        errorConsumidas.value = null;

        try {
            const result = await invitacionesService.getConsumidasPaginadas(abonadoId, {
                page,
                per_page: consumidasMeta.value.per_page,
            });
            consumidas.value = result.items;
            consumidasMeta.value = result.meta;
        } catch (err) {
            const normalized = toError(err);
            errorConsumidas.value = normalized.message ??
                t('perfil.invitaciones.error_consumidas', {}, 'Error al cargar el historial de invitaciones consumidas.');
            consumidas.value = [];
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useInvitaciones] Error al obtener consumidas paginadas:', err);
            }
        } finally {
            loadingConsumidasModal.value = false;
        }
    };

    // ─── Watch reactivo al ID del abonado (patrón useLiberarAsiento) ─────────
    watch(
        () => getAbonadoId(),
        newAbonadoId => {
            resetDashboard();
            if (newAbonadoId) {
                fetchDashboard();
            }
        },
        { immediate: true }
    );

    // ─── Listado paginado de invitaciones (para InvitacionesList) ────────────
    const {
        items: invitaciones,
        loading: loadingInvitaciones,
        error: errorInvitaciones,
        meta: invitacionesMeta,
        page: invitacionesPage,
        perPage: invitacionesPerPage,
        fetch: fetchInvitaciones,
    } = useFetchPaginated({
        vuexAlias:    'front.perfil.invitaciones',
        errorMessage: 'Error al cargar invitaciones',
    });

    // ─── API pública ──────────────────────────────────────────────────────────
    return {
        // State — stats
        stats,
        loadingStats,
        errorStats,

        // State — eventos
        eventos,
        loadingEventos,
        errorEventos,

        // State — consumidas (modal)
        consumidas,
        loadingConsumidasModal,
        errorConsumidas,
        consumidasMeta,
        showConsumidasModal,

        // State — listado paginado (InvitacionesList)
        invitaciones,
        loading: loadingInvitaciones,
        error: errorInvitaciones,
        meta: invitacionesMeta,
        page: invitacionesPage,
        perPage: invitacionesPerPage,
        fetchInvitaciones,

        // Computed guards — dashboard global
        isDashboardLoading,
        hasDashboardError,
        isStatsEmpty,
        hasInvitacionesDisponibles,

        // Computed guards — eventos
        hasEventos,
        isEventosEmpty,

        // Computed guards — modal consumidas
        hasConsumidasEnModal,
        isConsumidasModalEmpty,
        hasMoreConsumidasPages,

        // Funciones guard (no computed — reciben parámetro)
        canGenerarInvitaciones,

        // Métodos
        fetchDashboard,
        openConsumidasModal,
        closeConsumidasModal,
        fetchConsumidasPagina,
    };
}
